import React, { useState, useEffect } from "react";
import { useFlashCards } from "../../hooks/useFlashCards";
import { lang } from "../../lang";
import TopicList from "./TopicList";
import FlashCardEditor from "./FlashCardEditor";
import FlashCard from "./FlashCard";
// AddFlashCardForm is now rendered by TopicList
// import AddFlashCardForm from './AddFlashCardForm';
import "./FlashCardsPage.css";

function FlashCardsPage() {
  const {
    topics,
    cards,
    addTopic,
    deleteTopic,
    updateTopic,
    addCard,
    updateCard,
  } = useFlashCards();

  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [isCardEditorOpen, setIsCardEditorOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  useEffect(() => {
    if (!selectedTopicId && topics && topics.length > 0) {
      setSelectedTopicId(topics[0].id);
    }
    if (
      selectedTopicId &&
      topics &&
      !topics.find((t) => t.id === selectedTopicId)
    ) {
      setSelectedTopicId(topics.length > 0 ? topics[0].id : null);
    }
  }, [topics, selectedTopicId]);

  const handleSelectTopic = (topicId) => {
    setSelectedTopicId(topicId);
  };

  const handleUpdateTopicName = (topicId, newName) => {
    updateTopic(topicId, { name: newName });
  };

  const handleDeleteTopic = (topicId) => {
    if (
      window.confirm(
        `Are you sure you want to delete this topic and all its cards?`
      )
    ) {
      const topicIndex = topics.findIndex((t) => t.id === topicId);
      const remainingTopics = topics.filter((t) => t.id !== topicId);
      deleteTopic(topicId);

      if (selectedTopicId === topicId) {
        if (remainingTopics.length > 0) {
          const newIndex = Math.min(topicIndex, remainingTopics.length - 1);
          setSelectedTopicId(
            remainingTopics[Math.max(0, newIndex)]?.id || null
          );
        } else {
          setSelectedTopicId(null);
        }
      }
    }
  };

  // This function will be passed to TopicList, which then passes it to AddFlashCardForm
  const handleAddCardToSelectedTopic = (cardDataFromForm) => {
    if (!selectedTopicId) {
      alert("Error: No topic selected for new card.");
      return;
    }
    // AddFlashCardForm should pass data including topicId (or we add it here)
    // The current AddFlashCardForm already takes topicId as a prop and includes it.
    addCard({
      ...cardDataFromForm,
      topicId: selectedTopicId,
      difficulty: null,
    });
  };

  const handleSaveCardFromEditor = (cardData, cardIdToUpdate) => {
    if (cardIdToUpdate) {
      updateCard(cardIdToUpdate, cardData);
    }
    setIsCardEditorOpen(false);
    setEditingCard(null);
  };

  const handleOpenEditCardModal = (cardToEdit) => {
    setEditingCard(cardToEdit);
    setIsCardEditorOpen(true);
  };

  const handleUpdateCardDifficulty = (cardId, newDifficulty) => {
    updateCard(cardId, { difficulty: newDifficulty });
  };

  const selectedTopic = topics.find((t) => t.id === selectedTopicId);
  const topicCategories = selectedTopic ? selectedTopic.categories : [];
  const cardsForSelectedTopic = selectedTopicId
    ? cards.filter((card) => card.topicId === selectedTopicId)
    : [];

  return (
    <>
      <h2>Please pardon our dust - Flash cards are a work in progress</h2>
      <div className="flashcards-page-container">
        <div className="flashcards-left-rail">
          <div className="topic-management-header">
            <h2>{lang.flashCards.addTopic || "Topics"}</h2>
          </div>
          <TopicList
            topics={topics}
            selectedTopicId={selectedTopicId}
            onSelectTopic={handleSelectTopic}
            onAddTopic={addTopic}
            onDeleteTopic={handleDeleteTopic}
            onUpdateTopicName={handleUpdateTopicName}
            // Pass props needed for the nested AddFlashCardForm
            topicCategories={topicCategories} // Categories for the currently selected topic
            onAddCard={handleAddCardToSelectedTopic} // Handler to add card to selected topic
          />
          {/* AddFlashCardForm is now rendered inside TopicList for the selected topic */}
        </div>
        <div className="flashcards-main-area">
          <div className="flashcards-main-header">
            <h1>
              {selectedTopic
                ? selectedTopic.name
                : lang.app.flashCardsTab || "Flash Cards"}
            </h1>
          </div>

          {selectedTopicId ? (
            <div className="cards-display-area">
              {cardsForSelectedTopic.length > 0 ? (
                cardsForSelectedTopic.map((card) => (
                  <FlashCard
                    key={card.id}
                    card={card}
                    onUpdateCardDifficulty={handleUpdateCardDifficulty}
                    onEditCard={handleOpenEditCardModal}
                  />
                ))
              ) : (
                <p className="flashcard-placeholder-text">
                  No flashcards in this topic yet. Select this topic and add
                  some!
                </p>
              )}
            </div>
          ) : topics.length > 0 ? (
            <p className="flashcard-placeholder-text">
              Select a topic to view or add flashcards.
            </p>
          ) : (
            <p className="flashcard-placeholder-text">
              Create your first topic to get started!
            </p>
          )}
        </div>

        {editingCard && isCardEditorOpen && (
          <FlashCardEditor
            isOpen={isCardEditorOpen}
            onClose={() => {
              setIsCardEditorOpen(false);
              setEditingCard(null);
            }}
            onSave={handleSaveCardFromEditor}
            existingCardData={editingCard}
            topicCategories={topicCategories}
          />
        )}
      </div>
    </>
  );
}

export default FlashCardsPage;
